import { cloneDeep } from 'lodash';
import { cloneGridDraft, decodeGridState } from 'src/app/_helpers/grid-state';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { HelperService } from 'src/app/_services/helper.service';
import { Locations } from 'src/app/_global/locations';
import { StoresManager } from 'src/app/_global/stores-manager';
import { Tags } from 'src/app/_global/tags';
import { GridStatesService } from 'src/app/_services/grid-states.service';
import { ViewMode } from '../../products/product_filter';
import { Datacolumn } from '../datatable/datacolumn/datacolumn.component';
import { FilterOption } from '../filters/filters.component';
import StoresEntitiesState from '../../../states/stores/stores-entities.state';

@Component({
  selector: 'edit-grid-state',
  templateUrl: './edit-grid-state.component.html',
  styleUrls: ['./edit-grid-state.component.scss']
})
export class EditGridStateComponent implements OnInit {
  @Input() viewByOptions: ViewMode[];
  @Input() filterOptions: FilterOption[];
  @Input() savedFilters: any = {};
  @Input() gridState: any = { columns: [], gridColumns: {} };
  @Input() disabled: any = {};
  @Output() loading: EventEmitter<boolean> = new EventEmitter();
  @Output() saved: EventEmitter<any> = new EventEmitter();
  @Output() deleted: EventEmitter<any> = new EventEmitter();
  formGroup: UntypedFormGroup;
  dropdowns: any = {};
  selectedFilters: FilterOption[] = [];
  visibleColumns: any[] = [];
  hiddenColumns: any[] = [];

  get supportsSkuExclusions() {
    const id = (this.gridState?.gridId || '').toString();
    return id === 'Listings' || id === 'Inventory';
  }

  /** Only the Listings grid distinguishes the marketplace listing status (-1/0) from the product switch (-3/-4). */
  get supportsProductStatusTags() {
    return (this.gridState?.gridId || '').toString() === 'Listings';
  }

  readonly listingStatusHint = 'Status reported by the marketplace on the last sync';
  readonly productStatusHint = "The product's Active switch in InventoryAhead";

  constructor(
    public modal: NgbActiveModal,
    public modalService: NgbModal,
    private gridStatesService: GridStatesService,
    private translate: TranslateService,
    private toastr: ToastrService,
    public locations: Locations,
    public stores: StoresManager,
    public tags: Tags,
    public helperService: HelperService,
    private fb: UntypedFormBuilder,
    public storesEntitiesState: StoresEntitiesState
  ) { }

  saving = false;

  ngOnInit() {
    this.gridState = cloneGridDraft(this.gridState);
    this.savedFilters = cloneDeep(this.savedFilters);
    this.filterOptions = this.filterOptions || [];
    // Ensure gridColumns is an object (it may arrive as a JSON string)
    if (typeof this.gridState.gridColumns === 'string') {
      try {
        this.gridState.gridColumns = JSON.parse(this.gridState.gridColumns);
      } catch {
        this.gridState.gridColumns = {};
      }
    } else {
      this.gridState.gridColumns = this.gridState.gridColumns || {};
    }
    this.gridState.gridColumns.locations = this.gridState.gridColumns.locations || [];
    this.gridState.gridColumns.storesEntityMap = this.gridState.gridColumns.storesEntityMap || [];
    this.gridState.gridColumns.tags = this.gridState.gridColumns.tags || [];
    this.gridState.gridColumns.excludedTags = this.gridState.gridColumns.excludedTags || [];
    // Default hide for Listings/Inventory unless explicitly set in saved view
    if (this.supportsSkuExclusions && this.gridState.gridColumns.hideAmznGrSkus === undefined) {
      this.gridState.gridColumns.hideAmznGrSkus = true;
    }
    let filters = this.gridState.gridColumns.filters || {};
    this.visibleColumns = this.gridState.columns
      .filter(c => this.helperService.hasPermission(c.permissions) && !c.freezed && c.visible);
    this.hiddenColumns = this.gridState.columns
      .filter(c => this.helperService.hasPermission(c.permissions) && !c.freezed && !c.visible);
    this.selectedFilters = [];
    for (let key of Object.keys(filters))
      this.selectedFilters.push({ ...this.filterOptions.find(f => f.field == key), ...filters[key] });
    this.formGroup = this.fb.group({
      name: [this.gridState.name, this.gridState.name !== undefined ? [Validators.required] : []],
      isDefault: [this.gridState.isDefault]
    });
  }

  locationSelected(e: any, locationId: number) {
    if (e.target.checked)
      this.gridState.gridColumns.locations.push(locationId);
    else
      this.gridState.gridColumns.locations = this.gridState.gridColumns.locations.filter(id => id != locationId);
  }

  storeSelected(e: any, storeId: number) {
    if (e.target.checked)
      this.gridState.gridColumns.storesEntityMap.push(storeId);
    else
      this.gridState.gridColumns.storesEntityMap = this.gridState.gridColumns.storesEntityMap.filter(id => id != storeId);
  }

  private getAllTagIds(): number[] {
    const dynamic = (this.tags.getAll() || [])
      .map((t: any) => Number(t?.tagId ?? t?.id))
      .filter((v: number) => Number.isFinite(v));
    const ids = [-1, 0, ...(this.supportsProductStatusTags ? [-3, -4] : []), ...dynamic];
    return Array.from(new Set(ids));
  }

  isSelectAllTagsChecked(): boolean {
    return this.areAllTagsSelected(this.gridState?.gridColumns?.tags || []);
  }

  isSelectAllExcludedTagsChecked(): boolean {
    return this.areAllTagsSelected(this.gridState?.gridColumns?.excludedTags || []);
  }

  private areAllTagsSelected(values: any[]): boolean {
    const allIds = this.getAllTagIds();
    const selected = (values || [])
      .map((v: any) => Number(v))
      .filter((v: number) => Number.isFinite(v) && v !== -2);
    if (!allIds.length) return false;
    return allIds.every(id => selected.includes(id)) && selected.length === allIds.length;
  }

  tagSelected(e: any, tagId: number) {
    this.toggleTagSelection('tags', e, tagId);
  }

  excludedTagSelected(e: any, tagId: number) {
    this.toggleTagSelection('excludedTags', e, tagId);
  }

  private toggleTagSelection(field: 'tags' | 'excludedTags', e: any, tagId: number) {
    this.gridState.gridColumns[field] = this.gridState.gridColumns[field] || [];
    // -2 used to be "Apply All". We now treat it as "Select All / Deselect All".
    if (tagId === -2) {
      this.gridState.gridColumns[field] = e.target.checked ? this.getAllTagIds() : [];
      return;
    }

    if (e.target.checked) {
      this.gridState.gridColumns[field].push(tagId);
      // If any legacy -2 exists, remove it.
      this.gridState.gridColumns[field] = this.gridState.gridColumns[field].filter(id => id !== -2);
    }
    else
      this.gridState.gridColumns[field] = this.gridState.gridColumns[field].filter(id => id != tagId);
  }

  get HasVisibleColumns() {
    return this.gridState.columns.some(col => !col.freezed && col.visible);
  }

  move(item: any) {
    let ix = this.hiddenColumns.indexOf(item);
    item.visible = true;
    transferArrayItem(this.hiddenColumns, this.visibleColumns, ix, this.visibleColumns.length);
  }

  drop(event: CdkDragDrop<Datacolumn[]>, visible: boolean = true) {
    if (event.container === event.previousContainer) {
      moveItemInArray(visible ? this.visibleColumns : this.hiddenColumns, event.previousIndex, event.currentIndex);
    } else {
      event.previousContainer.data[event.previousIndex].visible = visible;
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
  }

  delete(content: any) {
    if (this.saving) return;
    this.modalService.open(content).result.then(() => {
      this.saving = true;
      this.loading.emit(true);
      this.gridStatesService.delete(this.gridState.id, this.gridState.gridId).subscribe({
        next: () => {
          this.toastr.success(this.translate.instant('GENERAL.MESSAGES.DELETED',
            { name: this.gridState.name || this.gridState.gridId }));
          this.saving = false;
          this.loading.emit(false);
          this.deleted.emit(this.gridState);
          this.modal.close();
        },
        error: () => { this.saving = false; this.loading.emit(false); }
      });
    }, () => {});
  }

  save() {
    if (this.saving) return;
    if (this.formGroup.invalid) {
      this.formGroup.controls.name.markAsTouched();
      return;
    }
    for (let i = 0; i < this.visibleColumns.length; i++) {
      moveItemInArray(this.gridState.columns,
        this.gridState.columns.indexOf(this.visibleColumns[i]), i);
    }
    this.gridState.gridColumns.allFilters = cloneDeep(this.savedFilters);
    this.gridState.gridColumns.filters = {};
    this.selectedFilters.forEach(filter => this.gridState.gridColumns.filters[filter.field]
      = { type: filter.type, value: filter.value });
    this.gridState.name = this.formGroup.value.name || this.gridState.name;
    this.gridState.isDefault = this.formGroup.value.isDefault;
    this.saving = true;
    this.loading.emit(true);
    this.gridStatesService.set(this.gridState).subscribe({
      next: result => {
        this.toastr.success(this.translate.instant('GENERAL.MESSAGES.UPDATED',
          { name: this.gridState.name || this.gridState.gridId }));
        this.gridState.id = result.id;
        this.gridState.gridColumns = decodeGridState(result.gridColumns);
        this.saving = false;
        this.loading.emit(false);
        this.saved.emit(this.gridState);
        this.modal.close();
      },
      error: () => { this.saving = false; this.loading.emit(false); }
    });
  }
}
